import { Dispatch, SetStateAction, useMemo } from 'react';
import PanoramaFishEyeRoundedIcon from '@mui/icons-material/PanoramaFishEyeRounded';
import { GridColDef, GridRowParams } from '@mui/x-data-grid-pro';
import { ExtendedVaultMatterModel } from './vault-export-page';
import SharedDataGridClient from '../../../shared/shared-data-grid-client';
import { useToolbar } from '../../../shared/shared-data-grid-toolbar-helper';
import VaultMattersGridToolbar from './vault-matters-grid-toolbar';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import {
  formatBytes,
  toCamelCase,
  getDeleteMatterPermissionState,
} from '../../../utils/vault-utils';
import {
  DisabledTooltipMenuItem,
  TooltipGridActionsCellItem,
} from '../../../shared/datagrids/tooltip-grid-actions-cell-item';
import { ViewType } from '../../../../types';
import {
  isAbleToSeeUIElement,
  VaultRoles,
} from '../../../utils/roles/client-roles';
import { Messages } from '../../../utils/message-constants';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import Visibility from '@mui/icons-material/Visibility';
import { useTheme } from '@mui/material/styles';

export interface VaultMattersTableViewProps {
  toolsRoles: string[];
  isPageLoading: boolean;
  paginatedMatters: ExtendedVaultMatterModel[];
  selectedMatters: string[];
  setSelectedMatters: Dispatch<SetStateAction<string[]>>;
  setOpenDeleteMatterDialog: Dispatch<SetStateAction<boolean>>;
  setOpenAddMatterDialog: Dispatch<SetStateAction<boolean>>;
  setSelectedMatterId: (id: string) => void;
  handleSelectMatter: (id: string) => void;
  handleEditMatter: (id: string) => void;
  matterView: 'tile' | 'table';
  setMatterView: Dispatch<SetStateAction<'tile' | 'table'>>;
  viewType: ViewType;
  canCreateMatter?: boolean;
}

export default function VaultMattersTableView({
  toolsRoles,
  isPageLoading,
  paginatedMatters,
  selectedMatters,
  setSelectedMatters,
  setOpenDeleteMatterDialog,
  setOpenAddMatterDialog,
  setSelectedMatterId,
  handleSelectMatter,
  handleEditMatter,
  matterView,
  setMatterView,
  viewType,
  canCreateMatter,
}: Readonly<VaultMattersTableViewProps>) {
  const theme = useTheme();
  const matterColumns: GridColDef<ExtendedVaultMatterModel>[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Matter Name',
        flex: 1,
        minWidth: 220,
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 1,
        minWidth: 280,
        renderCell: (params) => params.value || 'No description',
      },
      {
        field: 'state',
        headerName: 'Status',
        width: 140,
        renderCell: (params) => {
          const value = params.value as string;
          let statusColor = 'error.main';
          if (value === 'OPEN') statusColor = 'success.main';
          else if (value === 'CLOSED') statusColor = 'text.secondary';
          return (
            <Chip
              icon={
                <PanoramaFishEyeRoundedIcon
                  fontSize="small"
                  sx={{ color: 'inherit' }}
                />
              }
              label={toCamelCase(value)}
              size="small"
              sx={{
                color: statusColor,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: `${statusColor}33`,
                '& .MuiChip-icon': {
                  color: 'inherit',
                },
              }}
            />
          );
        },
      },
      {
        field: 'exportCount',
        headerName: 'Exports',
        width: 110,
        renderCell: (params) => {
          const exportCount = params.row.exportCount ?? 0;
          const exportsRunning = params.row.exportsRunning ?? 0;
          const exportsFailed = params.row.exportsFailed ?? 0;
          return (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ height: '100%' }}
            >
              <Typography variant="body2">{exportCount}</Typography>
              {exportCount > 0 && (
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor:
                      exportsFailed > 0
                        ? 'error.main'
                        : exportsRunning > 0
                        ? 'info.main'
                        : 'success.main',
                  }}
                />
              )}
            </Stack>
          );
        },
      },
      {
        field: 'dataSizeBytes',
        headerName: 'Data Size',
        width: 140,
        valueGetter: (_value, row) => formatBytes(row.dataSizeBytes),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        type: 'actions',
        width: 130,
        getActions: (params: GridRowParams<ExtendedVaultMatterModel>) => {
          const deleteState = getDeleteMatterPermissionState(toolsRoles);
          return [
            <TooltipGridActionsCellItem
              key="edit"
              icon={<Edit />}
              label="Edit"
              disabled={
                !isAbleToSeeUIElement(
                  toolsRoles,
                  VaultRoles.EditVaultMatters.value
                )
              }
              tooltipText={
                isAbleToSeeUIElement(
                  toolsRoles,
                  VaultRoles.EditVaultMatters.value
                )
                  ? ''
                  : Messages.Errors.Permission.Acting(
                      VaultRoles.EditVaultMatters.name
                    )
              }
              onClick={() => handleEditMatter(params.row.matterId)}
              showInMenu
            />,
            <TooltipGridActionsCellItem
              key="delete"
              icon={<Delete />}
              label="Delete"
              disabled={!deleteState.canDelete}
              tooltipText={deleteState.canDelete ? '' : deleteState.tooltip}
              onClick={() => {
                setSelectedMatterId(params.row.matterId);
                setOpenDeleteMatterDialog(true);
              }}
              showInMenu
            />,
          ];
        },
      },
    ],
    [
      setSelectedMatterId,
      setOpenDeleteMatterDialog,
      handleEditMatter,
      toolsRoles,
    ]
  );

  const matterGridToolbar = useToolbar(VaultMattersGridToolbar, {
    toolsRoles,
    selectedMatters,
    setOpenDeleteMatterDialog,
    onCreateMatter: () => {
      setSelectedMatterId('');
      setOpenAddMatterDialog(true);
    },
    matterView,
    setMatterView,
    canCreateMatter,
    vaultMatters: paginatedMatters,
  });

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        border:
          theme.palette.mode === 'dark'
            ? `0.5px solid ${theme.palette.semantic.outline.light}`
            : 0,
        borderRadius: 0,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        boxShadow: `0 1px 6px 0 ${theme.palette.semantic.boxShadow}`,
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <SharedDataGridClient
          slots={{
            toolbar: matterGridToolbar,
            ...createGridOverlays('Vault Matters', <Visibility />),
            baseMenuItem: DisabledTooltipMenuItem,
          }}
          columns={matterColumns}
          rows={paginatedMatters}
          getRowId={(row) => row.matterId}
          loading={isPageLoading}
          pagination={false}
          checkboxSelection
          disableRowSelectionOnClick
          hideFooterRowCount
          showToolbar={true}
          onRowClick={(params) => {
            handleSelectMatter(params.row.matterId);
          }}
          selectedRows={selectedMatters}
          setSelectedRows={setSelectedMatters}
          gridStateKey="vaultMattersGridStates"
          gridKey={viewType?.vaultMattersGridStates?.activeGridState}
          sx={{
            border: 'none',
            borderRadius: 0,
            '& .MuiDataGrid-footerContainer': { display: 'none' },
          }}
        />
      </Box>
    </Box>
  );
}
